import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { fetchBatchAddProjectEpisodes } from '@/service/api/project';
import ImageUploadBox from '@/components/common/ImageUploadBox';

interface BatchAddEpisodesModalProps {
  open: boolean;
  projectId: string;
  initialEpisodeNo: number;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

export default function BatchAddEpisodesModal({
  open,
  projectId,
  initialEpisodeNo,
  onClose,
  onSuccess,
  onError,
}: BatchAddEpisodesModalProps) {
  const [newEpisodesInput, setNewEpisodesInput] = useState<
    { episode_no: number; episode_title: string; upload_asset_id: string }[]
  >([{ episode_no: initialEpisodeNo, episode_title: '', upload_asset_id: '' }]);
  const [isSubmittingNew, setIsSubmittingNew] = useState(false);

  useEffect(() => {
    if (open) {
      setNewEpisodesInput([
        { episode_no: initialEpisodeNo, episode_title: '', upload_asset_id: '' },
      ]);
    }
  }, [open, initialEpisodeNo]);

  if (!open) return null;

  /** 批量新增剧集提交 */
  const handleBatchAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validItems = newEpisodesInput.filter(
      (item) => item.episode_no >= 0 && item.upload_asset_id.trim()
    );
    if (validItems.length === 0) {
      onError('请至少完成一集视频的上传');
      return;
    }
    setIsSubmittingNew(true);
    try {
      await fetchBatchAddProjectEpisodes(projectId, {
        episodes: validItems,
      });
      onSuccess(`成功添加 ${validItems.length} 集后续剧集！`);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : '批量添加后续剧集失败');
    } finally {
      setIsSubmittingNew(false);
    }
  };

  const handleUploadChange = (idx: number, assetId: string, file?: File) => {
    setNewEpisodesInput((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        let defaultTitle = item.episode_title;
        // 如果未填标题且上传了文件，自动填入文件名（无扩展名）
        if (!defaultTitle.trim() && file?.name) {
          defaultTitle = file.name.replace(/\.[^/.]+$/, '');
        }
        return {
          ...item,
          upload_asset_id: assetId,
          episode_title: defaultTitle,
        };
      })
    );
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form
        onSubmit={handleBatchAddSubmit}
        className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-xl border border-gray-100 animate-in fade-in zoom-in-95 duration-150"
      >
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
          <h4 className="font-bold text-foreground-900 text-base">新增后续剧集（批量提交）</h4>
          <button
            type="button"
            onClick={onClose}
            className="text-foreground-400 hover:text-foreground-700 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="mb-4 p-2.5 rounded-xl bg-blue-50/80 border border-blue-100 text-[11px] text-blue-700 flex items-center gap-1.5">
          <i className="ri-information-line text-sm text-blue-600 shrink-0"></i>
          <span>
            已记录最高集号为第 <strong className="font-semibold text-blue-900">{Math.max(initialEpisodeNo - 1, 0)}</strong> 集，本次自动从第 <strong className="font-semibold text-blue-900">{initialEpisodeNo}</strong> 集开始新增提交（可手动调整集号）。
          </span>
        </div>

        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1 mb-4">
          {newEpisodesInput.map((row, idx) => (
            <div
              key={idx}
              className="p-4 rounded-xl bg-gray-50/70 border border-gray-200/80 space-y-3 relative group"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-primary-700 bg-primary-50 border border-primary-100 px-2.5 py-0.5 rounded-full">
                  第 {row.episode_no} 集
                </span>
                {newEpisodesInput.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setNewEpisodesInput((prev) => prev.filter((_, i) => i !== idx))}
                    className="text-xs text-rose-500 hover:text-rose-700 flex items-center gap-0.5 transition-colors"
                  >
                    <i className="ri-delete-bin-line"></i> 删除此集
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
                <div className="sm:col-span-4 space-y-2">
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-600 mb-1">
                      集号 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min={0}
                      value={row.episode_no}
                      onChange={(e) => {
                        const raw = parseInt(e.target.value);
                        const val = isNaN(raw) ? 0 : raw;
                        setNewEpisodesInput((prev) =>
                          prev.map((item, i) => (i === idx ? { ...item, episode_no: val } : item))
                        );
                      }}
                      className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-300 focus:border-primary-500 outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-600 mb-1">
                      单集标题 (选填)
                    </label>
                    <input
                      type="text"
                      value={row.episode_title}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewEpisodesInput((prev) =>
                          prev.map((item, i) => (i === idx ? { ...item, episode_title: val } : item))
                        );
                      }}
                      placeholder={`第${row.episode_no}集`}
                      className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-300 focus:border-primary-500 outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="sm:col-span-8">
                  <ImageUploadBox
                    label="视频文件"
                    required
                    value={row.upload_asset_id}
                    onChange={(assetId, file) => handleUploadChange(idx, assetId, file)}
                    scene="APPLICATION"
                    policy="uploader-only"
                    isVideo={true}
                    accept="video/*,.mp4,.mov,.mkv"
                    placeholder={`上传第 ${row.episode_no} 集视频`}
                    subPlaceholder="支持 MP4, MOV, MKV 常见视频格式"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => {
            const nextNo = (newEpisodesInput[newEpisodesInput.length - 1]?.episode_no ?? -1) + 1;
            setNewEpisodesInput((prev) => [
              ...prev,
              { episode_no: nextNo, episode_title: '', upload_asset_id: '' },
            ]);
          }}
          className="w-full py-2.5 rounded-xl border border-dashed border-primary-300 text-primary-700 text-xs font-medium hover:bg-primary-50/50 mb-4 flex items-center justify-center gap-1 transition-colors cursor-pointer"
        >
          <i className="ri-add-line text-sm"></i> 继续增加一行后续剧集
        </button>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-gray-100 text-foreground-700 text-xs hover:bg-gray-200 transition-colors"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={isSubmittingNew || newEpisodesInput.every((i) => !i.upload_asset_id.trim())}
            className="px-5 py-2 rounded-xl bg-primary-500 text-foreground-950 font-medium text-xs hover:bg-primary-400 disabled:opacity-50 flex items-center gap-1 transition-colors"
          >
            {isSubmittingNew && <i className="ri-loader-4-line animate-spin"></i>}
            提交新增剧集
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
